import React, { createContext, useState } from 'react';
import { textTranslate } from '../services/translationService';

// Import all translation objects
import { navbarTexts, projectsTexts, slideshowTexts, homeTexts, aboutMeTexts, footerTexts, contactTexts } from './Texts';

export const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const [translations, setTranslations] = useState({
    navbar: navbarTexts,
    projects: projectsTexts,
    slideshow: slideshowTexts,
    home: homeTexts,
    aboutMe: aboutMeTexts,
    footer: footerTexts,
    contact: contactTexts
  });
  const [isLoading, setIsLoading] = useState(false);

  // Function to extract all text strings from an object
  const extractTextStrings = (obj, result = []) => {
    if (typeof obj !== 'object' || obj === null) {
      if (typeof obj === 'string') { result.push(obj); }
      return result;
    }

    for (const key in obj) {
      if (typeof obj[key] === 'string') { result.push(obj[key]); } 
      else if (typeof obj[key] === 'object' && obj[key] !== null) { extractTextStrings(obj[key], result); }
    }

    return result;
  };

  // Function to rebuild an object with translated strings
  const rebuildObjectWithTranslations = (originalObj, translatedStrings, sourceStrings) => {
    if (typeof originalObj !== 'object' || originalObj === null) {
      if (typeof originalObj === 'string') {
        const index = sourceStrings.indexOf(originalObj);
        return index !== -1 ? translatedStrings[index] : originalObj;
      }
      return originalObj;
    }

    const result = Array.isArray(originalObj) ? [] : {};
    
    for (const key in originalObj) {
      if (typeof originalObj[key] === 'string') {
        const index = sourceStrings.indexOf(originalObj[key]);
        result[key] = index !== -1 ? translatedStrings[index] : originalObj[key];
      } else if (typeof originalObj[key] === 'object' && originalObj[key] !== null) {
        result[key] = rebuildObjectWithTranslations(originalObj[key], translatedStrings, sourceStrings);
      } else {
        result[key] = originalObj[key];
      }
    }

    return result;
  };

  // Function to translate all website content when language changes
  const translateAllContent = async (targetLang) => {
    if (targetLang === 'en') {
      // If English is selected, use the original English translations
      setTranslations({
        navbar: navbarTexts,
        projects: projectsTexts,
        slideshow: slideshowTexts,
        home: homeTexts,
        aboutMe: aboutMeTexts,
        contact: contactTexts,
        footer: footerTexts,
      });
      return;
    }

    setIsLoading(true);
    try {
      // Extract all text strings from each section
      const navbarStrings = extractTextStrings(navbarTexts);
      const projectsStrings = extractTextStrings([projectsTexts]);
      const slideshowStrings = extractTextStrings(slideshowTexts);
      const homeStrings = extractTextStrings(homeTexts);
      const aboutMeStrings = extractTextStrings(aboutMeTexts);
      const contactStrings = extractTextStrings(contactTexts);
      const footerStrings = extractTextStrings(footerTexts);

      // Batch translate all strings
      const translatedNavbarStrings = await textTranslate(navbarStrings, targetLang);
      const translatedProjectsStrings = await textTranslate(projectsStrings, targetLang);
      const translatedSlideshowStrings = await textTranslate(slideshowStrings, targetLang);
      const translatedHomeStrings = await textTranslate(homeStrings, targetLang);
      const translatedAboutMeStrings = await textTranslate(aboutMeStrings, targetLang);
      const translatedContactStrings = await textTranslate(contactStrings, targetLang);
      const translatedFooterStrings = await textTranslate(footerStrings, targetLang);

      // Rebuild objects with translated strings
      const translatedNavbar = rebuildObjectWithTranslations(navbarTexts, translatedNavbarStrings, navbarStrings);
      const translatedProjects = rebuildObjectWithTranslations(projectsTexts, translatedProjectsStrings, projectsStrings);
      const translatedSlideshow = rebuildObjectWithTranslations(slideshowTexts, translatedSlideshowStrings, slideshowStrings);
      const translatedHome = rebuildObjectWithTranslations(homeTexts, translatedHomeStrings, homeStrings);
      const translatedAboutMe = rebuildObjectWithTranslations(aboutMeTexts, translatedAboutMeStrings, aboutMeStrings);
      const translatedContact = rebuildObjectWithTranslations(contactTexts, translatedContactStrings, contactStrings);
      const translatedFooter = rebuildObjectWithTranslations(footerTexts, translatedFooterStrings, footerStrings);

      setTranslations({
        navbar: translatedNavbar,
        projects: translatedProjects,
        slideshow: translatedSlideshow,
        home: translatedHome,
        aboutMe: translatedAboutMe,
        contact: translatedContact,
        footer: translatedFooter
      });
    } catch (error) {
      console.error('Translation error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const changeLanguage = (lang) => {
    setLanguage(lang);
    translateAllContent(lang);
  };

  return (
    <LanguageContext.Provider value={{ 
      language, 
      changeLanguage, 
      translations,
      isLoading
    }}>
      {children}
    </LanguageContext.Provider>
  );
};
